import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { getStripe } from './stripe';
import {
  BookingPayload,
  useBookingIntentStatus,
  useCreateBookingIntent,
} from './use-booking-intent';

/**
 * Optional user-facing label overrides. The shared lib ships minimal English
 * defaults; consuming surfaces (marketplace, public booking, widget) own their
 * translations and can pass localized strings through these props. `{amount}`
 * in `pay` is interpolated with the formatted amount.
 */
export interface BookingPaymentLabels {
  pay?: string; // default: "Pay {amount}"
  processing?: string; // default: "Processing…"
  preparing?: string; // default: "Preparing payment…"
  confirming?: string; // default: "Confirming your booking…"
  paymentFailed?: string; // default: "Payment failed"
  bookingFailed?: string; // default: "Payment succeeded but the booking could not be completed. Please try again."
  refunded?: string; // default: "That slot was just taken — you've been refunded."
}

const DEFAULT_LABELS: Required<BookingPaymentLabels> = {
  pay: 'Pay {amount}',
  processing: 'Processing…',
  preparing: 'Preparing payment…',
  confirming: 'Confirming your booking…',
  paymentFailed: 'Payment failed',
  bookingFailed:
    'Payment succeeded but the booking could not be completed. Please try again.',
  refunded: "That slot was just taken — you've been refunded.",
};

export interface BookingPaymentStepProps {
  subdomain: string;
  payload: BookingPayload;
  amount: number;
  currency: string;
  /** Absolute URL Stripe redirects to for async methods (Pix/Bancontact). */
  returnUrl: string;
  onBooked: (
    appointmentId: string | undefined,
    bookingReference: string | undefined,
  ) => void;
  onError?: (message: string) => void;
  labels?: BookingPaymentLabels;
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
}

/**
 * Inner form rendered inside <Elements>. Owns the Pay button, calls
 * confirmPayment, and (on inline card success) hands control back up to begin
 * status polling.
 */
function PaymentForm(props: {
  amount: number;
  currency: string;
  returnUrl: string;
  labels: Required<BookingPaymentLabels>;
  onConfirmed: () => void;
  onError?: (message: string) => void;
}) {
  const { amount, currency, returnUrl, labels, onConfirmed, onError } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMsg(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      // Declined cards / validation errors keep the user on-page. Redirect
      // methods (Pix/Bancontact) navigate away to return_url before reaching
      // here, so we never see their result inline.
      const message = error.message || labels.paymentFailed;
      setErrorMsg(message);
      setSubmitting(false);
      onError?.(message);
      return;
    }

    // Inline success (e.g. card) with no redirect required — start polling
    // the backend for the booking outcome. Keep the button disabled.
    onConfirmed();
  };

  const formattedAmount = formatAmount(amount, currency);
  const payLabel = labels.pay.replace('{amount}', formattedAmount);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="text-base font-medium text-gray-900">
        {formattedAmount}
      </div>
      <PaymentElement />
      {errorMsg ? (
        <div className="text-sm text-red-600" role="alert">
          {errorMsg}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="inline-flex items-center justify-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? labels.processing : payLabel}
      </button>
    </form>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-6 text-sm text-gray-600">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
      <span>{label}</span>
    </div>
  );
}

/**
 * Shared prepaid-booking payment step. On mount it creates a PaymentIntent for
 * `payload`, renders the Stripe Payment Element, and after the payment
 * confirms inline it polls the booking status until it reaches a terminal
 * state. Async/redirect payment methods leave the page to `returnUrl`; the
 * consuming surface handles the return-from-redirect flow on that URL.
 */
export function BookingPaymentStep(props: BookingPaymentStepProps) {
  const {
    subdomain,
    payload,
    amount,
    currency,
    returnUrl,
    onBooked,
    onError,
    labels: labelOverrides,
  } = props;

  const labels: Required<BookingPaymentLabels> = {
    ...DEFAULT_LABELS,
    ...(labelOverrides ?? {}),
  };

  const createIntent = useCreateBookingIntent(subdomain);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [terminalMsg, setTerminalMsg] = useState<string | null>(null);
  // The amount actually charged is authoritative from the backend intent
  // response (PricingService), not the `amount` prop the caller passes for the
  // initial estimate. This matters for MOBILE, where the prop is service-only
  // but the charge includes the travel fee — display the charged total so
  // "Pay X" always equals what the card is debited.
  const [chargedAmount, setChargedAmount] = useState<number | null>(null);
  const [chargedCurrency, setChargedCurrency] = useState<string | null>(null);

  // Kick off intent creation once on mount. The ref guards against React 19
  // StrictMode double-invocation in dev.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    createIntent.mutate(payload, {
      onSuccess: (result) => {
        setClientSecret(result.clientSecret);
        setPaymentIntentId(result.paymentIntentId);
        setChargedAmount(result.amount);
        setChargedCurrency(result.currency);
      },
      onError: (err) => {
        const message = err?.message || labels.paymentFailed;
        onError?.(message);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusQuery = useBookingIntentStatus(polling ? paymentIntentId : null);

  // React to terminal booking states once polling has begun.
  useEffect(() => {
    const data = statusQuery.data;
    if (!polling || !data) return;
    if (data.state === 'booked') {
      onBooked(data.appointmentId, data.bookingReference);
    } else if (data.state === 'refunded') {
      setTerminalMsg(labels.refunded);
      onError?.(labels.refunded);
      setPolling(false);
    } else if (data.state === 'failed') {
      setTerminalMsg(labels.bookingFailed);
      onError?.(labels.bookingFailed);
      setPolling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data, polling]);

  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: { theme: 'stripe' as const },
          }
        : undefined,
    [clientSecret],
  );

  // Creation error: nothing to show but the message (already surfaced via
  // onError). Render an inline message too so a standalone consumer isn't blank.
  if (createIntent.isError && !clientSecret) {
    const message = createIntent.error?.message || labels.paymentFailed;
    return (
      <div className="text-sm text-red-600" role="alert">
        {message}
      </div>
    );
  }

  if (!clientSecret || !elementsOptions) {
    return <Spinner label={labels.preparing} />;
  }

  if (terminalMsg) {
    return (
      <div className="text-sm text-red-600" role="alert">
        {terminalMsg}
      </div>
    );
  }

  if (polling) {
    return <Spinner label={labels.confirming} />;
  }

  return (
    <Elements stripe={getStripe()} options={elementsOptions}>
      <PaymentForm
        amount={chargedAmount ?? amount}
        currency={chargedCurrency ?? currency}
        returnUrl={returnUrl}
        labels={labels}
        onConfirmed={() => setPolling(true)}
        onError={onError}
      />
    </Elements>
  );
}
